import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'searchInput',
    'typePill',
    'statusSelect',
    'sortButton',
    'tableRow',
    'mobileCard',
    'emptyMessage'
  ]

  static values = {
    sortField: { type: String, default: 'title' },
    sortDirection: { type: String, default: 'asc' }
  }

  connect() {
    this.currentTypeFilter = 'all'
    this.currentStatusFilter = ''
    this.currentSearch = ''
  }

  // Search handler
  search(event) {
    this.currentSearch = event.target.value.toLowerCase().trim()
    this.applyFilters()
  }

  // Type pill filter handler
  filterByType(event) {
    event.preventDefault()
    const button = event.currentTarget
    const filterValue = button.dataset.filter

    // Update active state on pills
    this.typePillTargets.forEach((pill) => {
      pill.classList.remove('active')
    })
    button.classList.add('active')

    this.currentTypeFilter = filterValue
    this.applyFilters()
  }

  // Status select handler
  filterByStatus(event) {
    this.currentStatusFilter = event.target.value
    this.applyFilters()
  }

  // Sort toggle handler
  toggleSort(event) {
    event.preventDefault()

    // Toggle sort direction
    if (this.sortDirectionValue === 'asc') {
      this.sortDirectionValue = 'desc'
    } else {
      this.sortDirectionValue = 'asc'
    }

    this.sortRows()
    this.updateSortButtonText()
  }

  // Apply all filters
  applyFilters() {
    let visibleCount = 0

    // Filter table rows (desktop)
    this.tableRowTargets.forEach((row) => {
      const visible = this.matchesFilters(row)
      row.style.display = visible ? '' : 'none'
      if (visible) visibleCount++
    })

    // Filter mobile cards
    this.mobileCardTargets.forEach((card) => {
      const visible = this.matchesFilters(card)
      card.style.display = visible ? '' : 'none'
    })

    // Show/hide empty message
    this.updateEmptyState(visibleCount)
  }

  // Check if row matches current filters
  matchesFilters(element) {
    const pageType = element.dataset.pageType || ''
    const pageStatus = element.dataset.pageStatus || ''
    const pageTitle = (element.dataset.pageTitle || '').toLowerCase()
    const pageSlug = (element.dataset.pageSlug || '').toLowerCase()

    // Type filter
    if (this.currentTypeFilter !== 'all') {
      if (!pageType.toLowerCase().includes(this.currentTypeFilter.toLowerCase())) {
        return false
      }
    }

    // Status filter
    if (this.currentStatusFilter) {
      if (pageStatus !== this.currentStatusFilter) {
        return false
      }
    }

    // Search filter
    if (this.currentSearch) {
      if (!pageTitle.includes(this.currentSearch) && !pageSlug.includes(this.currentSearch)) {
        return false
      }
    }

    return true
  }

  // Sort table rows
  sortRows() {
    const tbody = this.element.querySelector('tbody')
    if (!tbody) return

    const rows = Array.from(this.tableRowTargets)

    rows.sort((a, b) => {
      const aTitle = (a.dataset.pageTitle || '').toLowerCase()
      const bTitle = (b.dataset.pageTitle || '').toLowerCase()

      let comparison = aTitle.localeCompare(bTitle)

      if (this.sortDirectionValue === 'desc') {
        comparison = -comparison
      }

      return comparison
    })

    // Re-append rows in sorted order
    rows.forEach((row) => tbody.appendChild(row))

    // Also sort mobile cards
    const mobileContainer = this.element.querySelector('[data-mobile-cards]')
    if (mobileContainer) {
      const cards = Array.from(this.mobileCardTargets)

      cards.sort((a, b) => {
        const aTitle = (a.dataset.pageTitle || '').toLowerCase()
        const bTitle = (b.dataset.pageTitle || '').toLowerCase()

        let comparison = aTitle.localeCompare(bTitle)

        if (this.sortDirectionValue === 'desc') {
          comparison = -comparison
        }

        return comparison
      })

      cards.forEach((card) => mobileContainer.appendChild(card))
    }
  }

  // Update sort button text
  updateSortButtonText() {
    if (!this.hasSortButtonTarget) return

    const text = this.sortButtonTarget.querySelector('[data-sort-text]')
    if (text) {
      text.textContent = this.sortDirectionValue === 'asc' ? 'A→Z' : 'Z→A'
    }
  }

  // Show empty state when no results
  updateEmptyState(visibleCount) {
    if (!this.hasEmptyMessageTarget) return

    if (visibleCount === 0 && (this.currentSearch || this.currentTypeFilter !== 'all' || this.currentStatusFilter)) {
      this.emptyMessageTarget.classList.remove('hidden')
    } else {
      this.emptyMessageTarget.classList.add('hidden')
    }
  }

  // Clear all filters
  clearFilters(event) {
    event.preventDefault()

    // Reset search
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = ''
    }
    this.currentSearch = ''

    // Reset type pills
    this.typePillTargets.forEach((pill) => {
      pill.classList.remove('active')
      if (pill.dataset.filter === 'all') {
        pill.classList.add('active')
      }
    })
    this.currentTypeFilter = 'all'

    // Reset status select
    if (this.hasStatusSelectTarget) {
      this.statusSelectTarget.value = ''
    }
    this.currentStatusFilter = ''

    this.applyFilters()
  }
}
